import { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { Loader2, CheckCircle, Calendar, Clock } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BookingModal = ({ isOpen, onClose }) => {
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        company: '',
        preferred_date: '',
        preferred_time: '',
        message: '',
    });

    const handleChange = (e) => {
        setFormData(prev => ({
            ...prev,
            [e.target.name]: e.target.value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            await axios.post(`${API}/bookings`, formData);
            setSubmitted(true);
            toast.success('Booking request received! We\'ll confirm shortly.');
        } catch (error) {
            console.error('Error submitting booking:', error);
            toast.error('Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        onClose();
        // Reset after animation
        setTimeout(() => {
            setSubmitted(false);
            setFormData({
                name: '',
                email: '',
                phone: '',
                company: '',
                preferred_date: '',
                preferred_time: '',
                message: '',
            });
        }, 300);
    };

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-lg" data-testid="booking-modal">
                <DialogHeader>
                    <DialogTitle className="text-2xl font-semibold">
                        Book a Strategy Call
                    </DialogTitle>
                </DialogHeader>

                {submitted ? (
                    <div className="text-center py-8" data-testid="booking-success">
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 mb-6">
                            <CheckCircle className="w-8 h-8 text-green-600 dark:text-green-400" />
                        </div>
                        <h3 className="text-xl font-semibold mb-2">Request Received!</h3>
                        <p className="text-muted-foreground mb-6">
                            We'll confirm your slot within 2 hours during business hours.
                        </p>
                        <Button onClick={handleClose} variant="outline" className="rounded-full">
                            Close
                        </Button>
                    </div>
                ) : (
                    <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="booking-name">Name *</Label>
                                <Input
                                    id="booking-name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    placeholder="John Doe"
                                    required
                                    data-testid="booking-name"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="booking-email">Email *</Label>
                                <Input
                                    id="booking-email"
                                    name="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder="john@company.com"
                                    required
                                    data-testid="booking-email"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="booking-phone">Phone *</Label>
                                <Input
                                    id="booking-phone"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    placeholder="+91 98765 43210"
                                    required
                                    data-testid="booking-phone"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="booking-company">Company</Label>
                                <Input
                                    id="booking-company"
                                    name="company"
                                    value={formData.company}
                                    onChange={handleChange}
                                    placeholder="Your Company"
                                    data-testid="booking-company"
                                />
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="booking-date">Preferred Date</Label>
                                <div className="relative">
                                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <Input
                                        id="booking-date"
                                        name="preferred_date"
                                        type="date"
                                        value={formData.preferred_date}
                                        onChange={handleChange}
                                        className="pl-10"
                                        min={new Date().toISOString().split('T')[0]}
                                        data-testid="booking-date"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="booking-time">Preferred Time</Label>
                                <div className="relative">
                                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <select
                                        id="booking-time"
                                        name="preferred_time"
                                        value={formData.preferred_time}
                                        onChange={handleChange}
                                        className="flex h-12 w-full rounded-lg border border-input bg-transparent pl-10 pr-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                                        data-testid="booking-time"
                                    >
                                        <option value="">Select time</option>
                                        <option value="10:00">10:00 AM</option>
                                        <option value="11:00">11:00 AM</option>
                                        <option value="12:00">12:00 PM</option>
                                        <option value="14:00">2:00 PM</option>
                                        <option value="15:00">3:00 PM</option>
                                        <option value="16:00">4:00 PM</option>
                                        <option value="17:00">5:00 PM</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="booking-message">What would you like to discuss?</Label>
                            <Textarea
                                id="booking-message"
                                name="message"
                                value={formData.message}
                                onChange={handleChange}
                                placeholder="Tell us about your business and goals..."
                                rows={3}
                                data-testid="booking-message"
                            />
                        </div>

                        <Button 
                            type="submit" 
                            className="w-full btn-glow rounded-full h-12"
                            disabled={loading}
                            data-testid="booking-submit"
                        >
                            {loading ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Scheduling...
                                </>
                            ) : (
                                'Request Strategy Call'
                            )}
                        </Button>

                        <p className="text-xs text-center text-muted-foreground">
                            Free 30-minute consultation • No obligations
                        </p>
                    </form>
                )}
            </DialogContent>
        </Dialog>
    );
};
